import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Typography } from '../typography';

import styles from './drawer.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    open: boolean;
    width?: number;
    hero?: boolean;
    vertical?: boolean;
    image?: string;
    title?: string;
    description?: string;
    buttons?: ReactNode;
    header?: ReactNode;
    shadow?: boolean;
    onShadowClick?: () => void;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-drawer';

const DEFAULT_WIDTH = 320;

export const Drawer: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      open,
      width = DEFAULT_WIDTH,
      hero,
      image,
      title,
      description,
      vertical,
      buttons,
      header,
      shadow,
      onShadowClick,
      children,
      style,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const [hasScroll, setHasScroll] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);

    const headerBackground = hero && image ? { backgroundImage: `url('${image}')` } : {};

    const drawerWidth = { width: width + 'px' };

    useEffect(() => {
      if (!open) return;

      if (contentRef.current.scrollHeight > contentRef.current.clientHeight) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }
    }, [open, children, header, buttons, title, description, image, hero, vertical]);

    return open ? (
      <>
        <div
          data-component-name={COMPONENT_ID}
          style={{ ...drawerWidth, ...style }}
          data-id={dataTestId}
          data-testid={dataTestId}
          className={classNames(className, styles.drawer, {
            [styles.open]: open,
          })}
          ref={ref}
          {...rest}>
          <div className={styles.container}>
            <div
              style={headerBackground}
              data-testid={`${dataTestId}-header`}
              className={classNames(styles.header, {
                [styles['with-image']]: image,
                [styles.hero]: image && hero,
                [styles.hidden]: header === null,
              })}>
              {header}
              {image && !hero ? (
                <div
                  className={styles.image}
                  data-testid={`${dataTestId}-image`}
                  style={{ backgroundImage: `url('${image}')` }}
                />
              ) : null}
            </div>
            <div data-testid={`${dataTestId}-content`} className={styles.content} ref={contentRef}>
              {title && (
                <Typography
                  className={styles.title}
                  dataTestId={`${dataTestId}-title`}
                  variant="large-title-semibold">
                  {title}
                </Typography>
              )}
              {description && (
                <Typography
                  dataTestId={`${dataTestId}-description`}
                  className={styles.description}
                  variant="body-regular">
                  {description}
                </Typography>
              )}
              {children}
            </div>
          </div>
          <div
            data-testid={`${dataTestId}-buttons`}
            className={classNames(styles['button-docked'], {
              [styles.vertical]: vertical,
              [styles['has-border']]: hasScroll,
            })}>
            {buttons}
          </div>
        </div>
        {shadow && (
          <div
            className={styles.shadow}
            style={style}
            data-testid={`${dataTestId}-shadow`}
            onClick={onShadowClick}
          />
        )}
      </>
    ) : null;
  },
);

Drawer.displayName = 'Drawer';
